import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MagnifyingGlass, Gift } from "@phosphor-icons/react";
import { toast } from "sonner";

export const GIFT_STATUS = {
  given: { label: "Given – awaiting member", cls: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200" },
  received: { label: "Received", cls: "bg-success/15 text-success" },
  problem: { label: "Problem reported", cls: "bg-destructive/10 text-destructive" },
};

export const GiftBadge = ({ status, testId }) => {
  const s = GIFT_STATUS[status];
  return s ? <span data-testid={testId} className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${s.cls}`}>{s.label}</span> : null;
};

const itemsText = (g) => [g?.tshirts ? "T-shirt" : "", g?.cloth ? "Cloth" : ""].filter(Boolean).join(" + ") || "—";

function StockCard({ label, stock, item }) {
  return (
    <Card className="p-4 border-t-4 border-t-primary" data-testid={`gift-stock-${item}`}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-display text-2xl font-bold mt-1">{stock.available[item]} <span className="text-sm font-normal text-muted-foreground">available</span></p>
      <p className="text-xs text-muted-foreground mt-1">
        {stock.confirmed[item] ? `${stock.received[item]} confirmed received · ${stock.given[item]} given out` : "Confirm receipt from your Region first"}
      </p>
    </Card>
  );
}

function GiftDialog({ member, onClose, onSaved }) {
  const g = member.gift;
  const [tshirts, setTshirts] = useState(!!g?.tshirts);
  const [cloth, setCloth] = useState(!!g?.cloth);
  const [note, setNote] = useState(g?.note || "");
  const [busy, setBusy] = useState(false);
  const save = async (remove = false) => {
    setBusy(true);
    try {
      await api.post("/souvenirs/gifts", { member_id: member.id, tshirts: remove ? 0 : Number(tshirts), cloth: remove ? 0 : Number(cloth), note });
      toast.success(remove ? "Record removed" : "Souvenirs recorded. The member can now confirm receipt.");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="gift-dialog">
        <DialogHeader>
          <DialogTitle>Give souvenirs to {member.name}</DialogTitle>
          <DialogDescription>Up to 1 T-shirt and 1 cloth per member. You can correct this until the member confirms receipt.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={tshirts} onCheckedChange={(v) => setTshirts(!!v)} data-testid="gift-tshirt-checkbox" />
            T-shirt {member.tshirt_size ? <span className="text-muted-foreground">(size {member.tshirt_size})</span> : <span className="text-muted-foreground">(no size on profile)</span>}</label>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={cloth} onCheckedChange={(v) => setCloth(!!v)} data-testid="gift-cloth-checkbox" /> Cloth</label>
          <div className="space-y-1.5"><Label htmlFor="gift-note">Note (optional)</Label>
            <Textarea id="gift-note" rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} data-testid="gift-note" /></div>
          {g?.status === "problem" && <p className="text-sm text-destructive" data-testid="gift-problem-note">Member reported: {g.member_note}</p>}
        </div>
        <DialogFooter className="gap-2">
          {g && <Button variant="outline" onClick={() => save(true)} disabled={busy} data-testid="gift-remove">Remove record</Button>}
          <Button onClick={() => save(false)} disabled={busy || (!tshirts && !cloth)} data-testid="gift-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function GiveToMembers() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    api.get("/souvenirs/gifts/members", { params: { search: search || undefined } })
      .then((r) => { setData(r.data); setError(""); })
      .catch((err) => setError(formatApiError(err.response?.data?.detail)));
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  if (error) return <p className="text-sm text-destructive" data-testid="gift-error">{error}</p>;
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-5" data-testid="give-to-members">
      <p className="text-sm text-muted-foreground">Record the souvenirs you hand to each approved member of <span className="font-medium text-foreground">{data.pool_label}</span>. Members then confirm receipt from their own portal.</p>
      <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">
        <StockCard label="T-shirts" item="tshirts" stock={data.stock} />
        <StockCard label="Cloth" item="cloth" stock={data.stock} />
      </div>
      <div className="relative max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-3 text-muted-foreground" />
        <Input className="pl-9" placeholder="Search member name or membership ID" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="gift-search" />
      </div>
      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Member</TableHead><TableHead>T-shirt size</TableHead><TableHead>Given</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.members.map((m) => (
              <TableRow key={m.id} data-testid={`gift-row-${m.id}`}>
                <TableCell><p className="font-medium">{m.name}</p><p className="text-xs font-mono text-muted-foreground">{m.membership_id}</p></TableCell>
                <TableCell>{m.tshirt_size || "—"}</TableCell>
                <TableCell>{itemsText(m.gift)}</TableCell>
                <TableCell>{m.gift ? <GiftBadge status={m.gift.status} testId={`gift-status-${m.id}`} /> : <span className="text-xs text-muted-foreground">Not yet given</span>}</TableCell>
                <TableCell className="text-right">
                  {m.gift?.status === "received" ? <span className="text-xs text-muted-foreground">Locked</span> : (
                    <Button size="sm" variant={m.gift ? "outline" : "default"} className="gap-1.5" onClick={() => setEditing(m)} data-testid={`gift-open-${m.id}`}>
                      <Gift size={14} weight="bold" /> {m.gift ? "Correct" : "Give"}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!data.members.length && <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">No approved members found.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
      {editing && <GiftDialog member={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
